import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePhaseDependencyRequest,
  CreatePhaseQueueGroupRequest,
  CreateProjectPlanRequest,
  ListPlanningEventsResponse,
  ListProjectPlansQuery,
  ProjectPlanDetails,
  UpdateProjectPhaseRequest,
} from '@kreart/shared';
import { projectPlanningApi } from './api';

const ROOT = ['project-planning'] as const;
export const projectPlanningQueryKeys = {
  all: ROOT,
  list: (query: ListProjectPlansQuery) => [...ROOT, 'list', query] as const,
  byProject: (projectId: string) => [...ROOT, 'project', projectId] as const,
  events: (planId: string | null | undefined) => [...ROOT, 'events', planId ?? 'nil'] as const,
};

export function invalidateProjectPlanningQueries(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ROOT });
}

export function useProjectPlans(query: ListProjectPlansQuery) {
  return useQuery({
    queryKey: projectPlanningQueryKeys.list(query),
    queryFn: () => projectPlanningApi.list(query),
    placeholderData: keepPreviousData,
    meta: { silent: true },
  });
}

export function usePlanningEvents(planId: string | null | undefined) {
  return useQuery<ListPlanningEventsResponse>({
    queryKey: projectPlanningQueryKeys.events(planId),
    queryFn: () => projectPlanningApi.listEvents(planId!),
    enabled: Boolean(planId),
    meta: { silent: true },
  });
}

export function useCreateProjectPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body?: CreateProjectPlanRequest }) =>
      projectPlanningApi.createForProject(projectId, body ?? {}),
    meta: { silent: true },
    onSuccess: (data: ProjectPlanDetails) => {
      qc.setQueryData(projectPlanningQueryKeys.byProject(data.projectId), data);
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useRemoveProjectPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => projectPlanningApi.removePlan(planId),
    meta: { silent: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useResetProjectPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => projectPlanningApi.resetPlan(planId),
    meta: { silent: true },
    onSuccess: (data: ProjectPlanDetails) => {
      qc.setQueryData(projectPlanningQueryKeys.byProject(data.projectId), data);
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useUpdateProjectPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: UpdateProjectPhaseRequest }) =>
      projectPlanningApi.updatePhase(phaseId, body),
    meta: { silent: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useAddPhaseDependency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, body }: { phaseId: string; body: CreatePhaseDependencyRequest }) =>
      projectPlanningApi.addDependency(phaseId, body),
    meta: { silent: true },
    onSuccess: (data: ProjectPlanDetails) => {
      qc.setQueryData(projectPlanningQueryKeys.byProject(data.projectId), data);
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useRemovePhaseDependency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dependencyId: string) => projectPlanningApi.removeDependency(dependencyId),
    meta: { silent: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function usePhaseAction(action: 'start' | 'complete' | 'block' | 'unblock') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, reason }: { phaseId: string; reason?: string }) => {
      if (action === 'start') return projectPlanningApi.startPhase(phaseId);
      if (action === 'complete') return projectPlanningApi.completePhase(phaseId);
      if (action === 'block')
        return projectPlanningApi.blockPhase(phaseId, { reason: reason ?? '' });
      return projectPlanningApi.unblockPhase(phaseId);
    },
    meta: { silent: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useCreatePhaseQueueGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePhaseQueueGroupRequest) => projectPlanningApi.createQueueGroup(body),
    meta: { silent: true },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ROOT }),
  });
}
